import { useQuery, QueryKey, QueryFunctionContext, RefetchOptions, QueryObserverResult } from 'react-query';
import { toastr } from 'react-redux-toastr';

import fetch from '../../utils/fetchService';
import Config from '../../config';
import { handleErrors, parseAsJson } from '../../utils/requestHelpers';
import { validateQueryKeys, createValidateQueryKey, useAutoMemo } from './utils';
import { useCallback } from 'react';

export const QueryKeys = Object.freeze({
  accounts: {
    AccountsList: 'accounts_AccountsList',
  },
  scenarios: {
    AllowedPeriods: 'scenarios_AllowedPeriods',
    ScenariosList: 'scenarios_ScenariosList',
    ScenarioMetadata: 'scenarios_ScenarioMetadata',
    ScenarioReportsData: 'scenarios_ScenarioReportsData',
    ScenarioReportsCompareData: 'scenarios_ScenarioReportsCompareData',
    ScenarioDetailsList: 'scenarios_ScenarioDetailsList',
    ScenarioRowDetailsList: 'scenarios_ScenarioRowDetailsList',
    ScenarioMembersList: 'scenarios_MembersList',
    ScenarioDetailsForExport: 'scenarios_ScenarioDetailsForExport',
    ScenarioAllowedAssignments: 'scenarios_ScenarioAllowedAssignments',
  },
  roles: {
    RolesList: 'roles_RolesList',
    RoleData: 'roles_RoleData',
    RoleCheckPrivilege: 'roles_RolesCheckPrivilege',
  },
  users: {
    ExcelData: 'users_ExcelData',
    ExpirationList: 'users_ExpirationList',
  },
  client: {
    ClientListForUser: 'client_ClientListForUser',
    ClientList: 'client_ClientList',
  },
  scenariosMembers: {
    MembersList: 'scenariosMembers_MembersList',
    LookUpData: 'scenariosMembers_LookUpData',
    AddMember: 'scenariosMembers_AddMember',
    EditMember: 'scenariosMembers_EditMember',
    IncludeMembersInScenario: 'scenariosMembers_IncludeInScenario',
    ExcludeMembersFromScenario: 'scenariosMembers_ExcludeMembersFromScenario',
  },
  scenariosObligations: {
    ObligationsList: 'scenariosObligations_ObligationsList',
    LookUpData: 'scenariosObligations_LookUpData',
    AddObligation: 'scenariosObligations_AddObligation',
    EditObligation: 'scenariosObligations_EditObligation',
  },
  currencies: {
    CurrenciesList: 'currencies_CurrenciesList',
    TaxCurrencyDefaultCodes: 'currencies_TaxCurrencyDefaultCodes',
    AdminCurrencies: 'currencies_AdminCurrencies',
  },
  countries: {
    CountriesList: 'countries_CountriesList',
  },
  countryTaxTypes: {
    CountryTaxTypesList: 'countryTaxTypes_CountryTaxTypesList',
  },
  clientAdministration: {
    ConnectionsList: 'clientAdministration_ConnectionsList',
    ConnectionTypeValues: 'clientAdministration_ConnectionTypeValues',
    InboundFormatValues: 'clientAdministration_InboundFormatValues',
    OutboundFormatValues: 'clientAdministration_OutboundFormatValues',
    YesNoFlagValues: 'clientAdministration_YesNoFlagValues',
    MaintenanceFlag: 'clientAdministration_MaintenanceFlag',
    LockFlag: 'clientAdministration_LockFlag',
  },
  businessUnits: {
    BusinessUnitsList: 'businessUnits_BusinessUnitsList',
  },
  bookToTaxCodes: {
    BookToTaxCodes: 'bookToTaxCodes_BookToTaxCodes',
  },
  batchReports: {
    BatchReport: 'batchReports_BatchReport',
    DownloadBatchReport: 'batchReports_DownloadBatchReport',
    DownloadBatchZipReport: 'batchReports_DownloadBatchZipReport',
    ReportParams: 'batchReports_ReportParams',
    BatchReportArchiveCompanyCodes: 'batchReports_BatchReportArchiveCompanyCodes',
  },
  journalFiles: {
    DownloadJournalFile: 'journalFiles_DownloadJournalFile',
  },
  helpPage: {
    UserHelpClients: 'helpPage_UserHelpClients',
  },
  session: {
    SessionStatus: 'session_SessionStatus',
  },
  tlsAccounts: {
    TlsAccountsList: 'tlsAccounts_TlsAccountsList',
  },
  customGroups: {
    CustomGroups: 'customGroups_CustomGroups',
    CustomGroupsWithCompanyCodes: 'customGroups_CustomGroupsWithCompanyCodes',
  },
  periods: {
    CurrentPeriod: 'periods_CurrentPeriod',
  },
  customReports: {
    Params: 'customReports_Params',
    CustomReportInsertQuery: 'customReports_CustomReportInsertQuery',
  },
  diagnostics: {
    TaxBooking: 'diagnostics_TaxBooking',
    Currencies: 'diagnostics_Currencies',
    Obligation: 'diagnostics_Obligation',
    CompanyCode: 'diagnostics_CompanyCode',
    LegalEntity: 'diagnostics_LegalEntity',
    Provision: 'diagnostics_Provision',
  },
  idleProcess: {
    Values: 'idleProcess_Values',
  },
  jobs: {
    JobMetadata: 'jobs_JobMetadata',
  },
  reports: {
    TaxExpenseRowDetails: 'reports_TaxExpenseRowDetails',
  },
});

validateQueryKeys(QueryKeys);
const validateKey = createValidateQueryKey(QueryKeys);

export type CustomQueryOptions<TQueryData, TError, TData = TQueryData> = {
  url?: string,
  errorMessage?: string,
  initialData?: TData,
  placeholderData?: TData,
  defaultData?: TData,
  onSuccess?: (data: TData) => void,
  onError?: (error: TError) => void,
  enabled?: boolean,
  refetchInterval?: false | number,
  customFetch?: (queryContext: QueryFunctionContext) => TData | Promise<TData>,
  retry?: number | boolean | ((failureCount: number, error: Error) => boolean),
  select?: (data: TQueryData) => TData,
  showErrorToastr?: boolean,
  parser?: ((data: TData) => TData | Promise<TData>),
  forceCookie?: boolean,
};

export type QueryError = Error & {
  isAuthorizationError: boolean,
  isFetchingError: boolean,
  message: string,
};

const isQueryError = (error: Error | QueryError): error is QueryError =>
  typeof (error as QueryError).isAuthorizationError !== 'undefined'
    && typeof (error as QueryError).isFetchingError !== 'undefined'
    && typeof (error as QueryError).message !== 'undefined';

const identity = <TData>(data: TData) => data;

export const useCustomQuery = <TData, TError extends (Error | QueryError) = QueryError>(
  key: QueryKey,
  {
    url,
    errorMessage,
    initialData,
    placeholderData,
    defaultData: defaultDataRaw,
    onSuccess,
    onError,
    enabled,
    customFetch,
    refetchInterval,
    retry = false,
    select,
    showErrorToastr = true,
    parser = identity,
    forceCookie = false,
  }: CustomQueryOptions<TData, TError>
) => {
  const defaultData = useAutoMemo(defaultDataRaw);

  const queryStatus = useQuery<TData, TError>(
    key,
    queryContext => {
      validateKey(Array.isArray(key) ? key[0] : key);
      if (customFetch) {
        return customFetch(queryContext);
      }
      return fetch(url, {}, forceCookie)
        .then(handleErrors({ errorMessage }))
        .then(parseAsJson)
        .then(parser);
    },
    {
      enabled,
      initialData,
      placeholderData,
      retry,
      select,
      refetchInterval,
      onSuccess: data => {
        if (onSuccess) {
          onSuccess(data);
        }
      },
      onError: error => {
        if (isQueryError(error)) {
          if (error.isAuthorizationError) {
            return;
          }
          if (error.isFetchingError) {
            if (showErrorToastr) {
              toastr.error('', error.message, Config.defaultToastrSettings);
            }
            return;
          }
        }
        if (onError) {
          return onError(error);
        }
        throw error;
      },
    }
  );

  const queryStatusRefetch = queryStatus.refetch;

  const getResultData = useCallback((data: unknown) =>
    (typeof defaultData !== 'undefined' && typeof data === 'undefined'
      ? defaultData
      : data) as TData, [defaultData]);

  const refetch = useCallback(async (options?: RefetchOptions | undefined): Promise<
    Omit<QueryObserverResult<TData, TError>, 'data'> & { data: TData }
    > => {
    const { data, ...queryResults } = await queryStatusRefetch(options);

    return {
      ...queryResults,
      data: getResultData(data),
    };
  }, [getResultData, queryStatusRefetch]);

  return {
    ...queryStatus,
    refetch,
    data: getResultData(queryStatus.data),
  };
};
